import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BookOpen, Pencil, Plus, Power, Save, Search, Tag, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { id: null, name: "", subcategories: [], is_active: true };

export const FinanceChartOfAccounts = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null);
  const [subcategory, setSubcategory] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/finance/categories`);
      setCategories(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar o plano de contas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    if (!term) return categories;
    return categories.filter((category) => (
      category.name.toLocaleLowerCase("pt-BR").includes(term)
      || (category.subcategories || []).some((item) => item.toLocaleLowerCase("pt-BR").includes(term))
    ));
  }, [categories, search]);

  const startEdit = (category = EMPTY_FORM) => {
    setForm({
      id: category.id || null,
      name: category.name || "",
      subcategories: [...(category.subcategories || [])],
      is_active: category.is_active !== false,
    });
    setSubcategory("");
  };

  const addSubcategory = () => {
    const value = subcategory.trim();
    if (!value || form.subcategories.some((item) => item.toLocaleLowerCase("pt-BR") === value.toLocaleLowerCase("pt-BR"))) return;
    setForm((current) => ({ ...current, subcategories: [...current.subcategories, value] }));
    setSubcategory("");
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome da categoria.");
    const pendingSubcategory = subcategory.trim();
    const subcategories = [...form.subcategories];
    if (
      pendingSubcategory
      && !subcategories.some((item) => item.toLocaleLowerCase("pt-BR") === pendingSubcategory.toLocaleLowerCase("pt-BR"))
    ) {
      subcategories.push(pendingSubcategory);
    }
    setSaving(true);
    try {
      await axios.post(`${API}/finance/categories`, {
        ...form,
        name: form.name.trim(),
        subcategories,
        directions: ["receita", "despesa"],
      });
      toast.success(form.id ? "Categoria atualizada." : "Categoria criada para pagamentos e recebimentos.");
      setForm(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar categoria.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (category) => {
    try {
      await axios.post(`${API}/finance/categories`, {
        id: category.id,
        name: category.name,
        subcategories: category.subcategories || [],
        directions: ["receita", "despesa"],
        is_active: category.is_active === false,
      });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar categoria.");
    }
  };

  return (
    <section className="space-y-5" data-testid="finance-chart-of-accounts">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <BookOpen className="w-5 h-5" />
            <h3 className="font-serif text-xl font-bold text-white">Plano de Contas</h3>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            As mesmas categorias e subcategorias ficam disponíveis em pagamentos e recebimentos.
          </p>
        </div>
        <button
          data-testid="chart-accounts-add-category"
          onClick={() => startEdit()}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4AF37] px-4 py-2 text-xs font-bold uppercase text-slate-950 hover:bg-[#C5A028]"
        >
          <Plus className="w-4 h-4" /> Nova categoria
        </button>
      </header>

      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="chart-accounts-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar categoria ou subcategoria…"
          className="w-full rounded-lg border border-slate-700 bg-[#080C14] py-2.5 pl-10 pr-3 text-sm text-white focus:border-[#D4AF37] focus:outline-none"
        />
      </div>

      {form && (
        <div className="border-y border-[#D4AF37]/30 bg-[#D4AF37]/5 py-5" data-testid="chart-accounts-editor">
          <div className="grid gap-4 lg:grid-cols-[minmax(240px,0.7fr)_1.3fr]">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">Categoria</label>
              <input
                data-testid="chart-accounts-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="Ex.: Material Litúrgico"
                className="w-full rounded-lg border border-slate-700 bg-[#080C14] p-2.5 text-sm text-white focus:border-[#D4AF37] focus:outline-none"
              />
              <span className="mt-2 inline-flex rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[10px] font-bold uppercase text-emerald-300">
                Crédito e Débito
              </span>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">Subcategorias</label>
              <div className="flex gap-2">
                <input
                  data-testid="chart-accounts-subcategory-input"
                  value={subcategory}
                  onChange={(event) => setSubcategory(event.target.value)}
                  onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addSubcategory(); } }}
                  placeholder="Ex.: Material recebido de Irmão"
                  className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-[#080C14] p-2.5 text-sm text-white focus:border-[#D4AF37] focus:outline-none"
                />
                <button data-testid="chart-accounts-add-subcategory" onClick={addSubcategory} className="rounded-lg border border-[#D4AF37]/40 px-3 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {form.subcategories.map((item, index) => (
                  <span key={`${item}-${index}`} className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-[#131C2E] px-2 py-1 text-xs text-slate-200">
                    {item}
                    <button data-testid={`chart-accounts-remove-subcategory-${index}`} onClick={() => setForm((current) => ({ ...current, subcategories: current.subcategories.filter((_, itemIndex) => itemIndex !== index) }))}>
                      <X className="h-3 w-3 text-slate-500 hover:text-rose-300" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button data-testid="chart-accounts-cancel" onClick={() => setForm(null)} className="px-4 py-2 text-xs font-bold uppercase text-slate-400 hover:text-white">Cancelar</button>
            <button data-testid="chart-accounts-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#D4AF37] px-5 py-2 text-xs font-bold uppercase text-slate-950 disabled:opacity-40">
              <Save className="h-4 w-4" /> {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-800 border-y border-slate-800" data-testid="chart-accounts-list">
        {loading ? <p className="py-8 text-center text-sm text-slate-500">Carregando…</p> : filtered.map((category) => (
          <div key={category.id} data-testid={`chart-account-row-${category.id}`} className={`grid gap-3 py-4 md:grid-cols-[minmax(220px,0.7fr)_1.3fr_auto] md:items-center ${category.is_active === false ? "opacity-50" : ""}`}>
            <div>
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-[#D4AF37]" />
                <strong className="text-sm text-white">{category.name}</strong>
              </div>
              <span className="ml-6 text-[10px] font-bold uppercase text-emerald-300">Crédito e Débito</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(category.subcategories || []).length > 0
                ? category.subcategories.map((item) => <span key={item} className="rounded bg-slate-800 px-2 py-1 text-[11px] text-slate-300">{item}</span>)
                : <span className="text-xs italic text-slate-600">Sem subcategorias</span>}
            </div>
            <div className="flex justify-end gap-2">
              <button data-testid={`chart-account-edit-${category.id}`} onClick={() => startEdit(category)} title="Editar" className="rounded-md border border-slate-700 p-2 text-slate-300 hover:border-[#D4AF37] hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`chart-account-toggle-${category.id}`} onClick={() => toggle(category)} title={category.is_active === false ? "Ativar" : "Desativar"} className="rounded-md border border-slate-700 p-2 text-slate-300 hover:border-rose-400 hover:text-rose-300"><Power className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};